import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./AddressPopup.module.css";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import { caseActions } from "@_actions/case.actions";
import { validateBillingAddress } from "./utils/billingValidation";
import { useBillingAddressSeed } from "./hooks/useBillingAddressSeed";
import BillingForm from "./BillingForm";

/**
 * AddressPopup
 *
 * Dialog for collecting a billing address and dispatching invoice generation.
 * Owns seed/validate/submit; BillingForm owns the field layout.
 * Does not close itself on success — CaseActivitySection does that when
 * CASE_GENERATE_INVOICE is "success".
 *
 * Props:
 *   case    – case object (billing_address, opponent_contact_details, vat_id_required)
 *   open    – whether the dialog is visible
 *   onClose – called on Cancel or dialog close (not on Generate invoice)
 */
function AddressPopup(props) {
  const dispatch = useDispatch();
  const caseInfo = props.case;
  const caseReducer = useSelector((state) => state.caseReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // Reference is stable during in-flight invoice generation because
  // CASE_GENERATE_INVOICE_REQUEST only updates loading/status, not case[caseId],
  // so the seeding effect won't re-run and clobber in-progress edits.
  // (CASE_GENERATE_INVOICE_SUCCESS and INVOICE_CANCEL_SUCCESS do replace
  // billing_address, but only after edits are dispatched — that's intentional.)
  // If a future action updates case[caseId] mid-flight, memoise this via a
  // stable key (scalar fields or JSON.stringify) or a deep-equality selector.
  const billingAddressSource = caseInfo?.billing_address;
  const country = caseReducer.case[caseInfo.id]?.country || "";

  const { billingAddress, updateBillingField, updateCustomFieldValue } =
    useBillingAddressSeed({
      caseId: caseInfo.id,
      billingAddressSource,
      contactDetails: caseInfo.opponent_contact_details,
      country,
    });

  const billingAddressValid = validateBillingAddress(billingAddress, {
    vatRequired: caseInfo.vat_id_required,
  });

  const generateInvoice = () => {
    setIsClicked(true);
    if (!billingAddressValid) {
      return;
    }
    dispatch(caseActions.generateInvoice(caseInfo.id, billingAddress));
    setIsClicked(false);
    setIsLoading(true);
  };

  useEffect(() => {
    if (caseReducer.status.CASE_GENERATE_INVOICE !== "request") {
      setIsLoading(false);
    }
  }, [caseReducer.status.CASE_GENERATE_INVOICE]);

  const handleClose = () => {
    setIsLoading(false);
    setIsClicked(false);
    props.onClose();
  };

  return (
    <DialogPopup
      title="Billing information"
      open={props.open}
      onClose={handleClose}
    >
      <div className={styles.address_modal}>
        <div className={styles.billing_container}>
          <div data-testid="address-popup-form">
            <BillingForm
              billingAddress={billingAddress}
              updateBillingField={updateBillingField}
              updateCustomFieldValue={updateCustomFieldValue}
              isClicked={isClicked}
              vatRequired={caseInfo.vat_id_required}
            />
          </div>
          <div className={styles.actionsContainer}>
            {isLoading ? (
              <div
                className={styles.loading}
                data-testid="address-popup-loading"
              >
                <CircularProgress color="primary" />
              </div>
            ) : (
              <div className="action_container">
                <Button
                  className="btn_cancel"
                  data-testid="address-popup-cancel-button"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={generateInvoice}
                  className="btn_confirm"
                  data-testid="address-popup-generate-button"
                  style={{ width: "165px" }}
                >
                  Generate invoice
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DialogPopup>
  );
}

export default AddressPopup;
